import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { ProjectConfiguration, ConsultationCategory } from "@/data/types";
import type { ProductOption } from "@/data/ProductOptionsContext";

interface ArrivalData {
  name: string;
  phone: string;
  address: string;
  primaryEmail: string;
  secondaryName?: string;
  secondaryPhone?: string;
  secondaryEmail?: string;
}

interface DiscoveryData {
  category: ConsultationCategory | "";
  painPoints: string[];
  notes: string;
}

interface PricingData {
  standardPrice: number;
  promoPrice: number;
  chosenPrice: number;
  selectedPayment: "cash" | "financing" | null;
  depositAmount: number;
}

interface ProjectScope {
  id: string;
  category: ConsultationCategory;
  painPoints: string[];
  notes: string;
  config: ProjectConfiguration;
  noAddOns: boolean;
}

interface BuildSummaryItem {
  label: string;
  value: string;
  category: "core" | "accessory" | "addon";
}

export interface ContractPDFData {
  companyName: string;
  contractId: string;
  date: string;
  arrival: ArrivalData;
  discovery: DiscoveryData;
  config: ProjectConfiguration;
  allOptions: ProductOption[];
  completedScopes: ProjectScope[];
  isMultiScope: boolean;
  pricing: PricingData;
  homeownerSig?: string;
  repSig?: string;
  homeownerSignedAt?: string;
  repSignedAt?: string;
}

function deriveBuildSummaryItems(config: ProjectConfiguration, allOptions: ProductOption[]): BuildSummaryItem[] {
  const items: BuildSummaryItem[] = [];
  const optName = (id: string) => allOptions.find(o => o.id === id)?.displayName;

  if (config.bathroomSystemType) {
    items.push({ label: "System", value: config.bathroomSystemType, category: "core" });
  }
  if (config.bathroomMaterials.length) {
    items.push({ label: "Material", value: config.bathroomMaterials.join(", "), category: "core" });
  }
  if (config.baseWidth && config.baseLength) {
    items.push({ label: "Base Size", value: `${config.baseWidth}" × ${config.baseLength}"`, category: "core" });
  }
  if (config.baseType) {
    items.push({ label: "Base Type", value: config.baseType, category: "core" });
  }
  if (config.baseStyle) {
    items.push({ label: "Base Style", value: config.baseStyle, category: "core" });
  }

  if (config.selectedWallSystem) {
    const n = optName(config.selectedWallSystem);
    if (n) items.push({ label: "Wall System", value: n, category: "core" });
  }
  if (config.selectedGlass) {
    const n = optName(config.selectedGlass);
    if (n) items.push({ label: "Glass", value: n, category: "core" });
  }
  if (config.selectedDoor) {
    const n = optName(config.selectedDoor);
    if (n) items.push({ label: "Door", value: n, category: "core" });
  }
  if (config.selectedFixture) {
    const n = optName(config.selectedFixture);
    if (n) items.push({ label: "Fixtures", value: n, category: "accessory" });
  }

  if (config.selectedGrabBars.length > 0) {
    items.push({
      label: "Grab Bars",
      value: config.selectedGrabBars.map(id => optName(id) || id).join(", "),
      category: "accessory",
    });
  }
  if (config.selectedAddOns.length > 0) {
    items.push({
      label: "Add-ons",
      value: config.selectedAddOns.map(id => optName(id) || id).join(", "),
      category: "addon",
    });
  }

  if (config.wallColor) {
    items.push({ label: "Wall Color", value: config.wallColor, category: "core" });
  }
  if (config.baseColor) {
    items.push({ label: "Base Color", value: config.baseColor, category: "core" });
  }
  if (config.colorSelection && !config.wallColor && !config.baseColor) {
    items.push({ label: "Color Selection", value: config.colorSelection, category: "core" });
  }

  if (config.enclosureType) {
    items.push({ label: "Enclosure", value: config.enclosureType, category: "core" });
  }
  if (config.hardwareFinish) {
    items.push({ label: "Hardware Finish", value: config.hardwareFinish, category: "accessory" });
  }

  return items;
}

export function generateContractPDF(data: ContractPDFData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(data.companyName, pageWidth / 2, y, { align: "center" });
  y += 8;

  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text("Service Agreement", pageWidth / 2, y, { align: "center" });
  y += 10;

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Contract ID: ${data.contractId}`, pageWidth / 2, y, { align: "center" });
  doc.text(`Date: ${data.date}`, pageWidth / 2, y + 5, { align: "center" });
  y += 15;

  doc.setDrawColor(200);
  doc.line(20, y, pageWidth - 20, y);
  y += 10;

  doc.setTextColor(0);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Customer Information", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Name: ${data.arrival.name}`, 20, y);
  y += 6;
  doc.text(`Phone: ${data.arrival.phone}`, 20, y);
  y += 6;
  doc.text(`Email: ${data.arrival.primaryEmail}`, 20, y);
  y += 6;
  doc.text(`Address: ${data.arrival.address}`, 20, y);
  y += 6;

  if (data.arrival.secondaryName) {
    doc.text(`Secondary Contact: ${data.arrival.secondaryName}`, 20, y);
    y += 6;
    if (data.arrival.secondaryPhone) {
      doc.text(`  Phone: ${data.arrival.secondaryPhone}`, 20, y);
      y += 6;
    }
    if (data.arrival.secondaryEmail) {
      doc.text(`  Email: ${data.arrival.secondaryEmail}`, 20, y);
      y += 6;
    }
  }
  y += 5;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Project Discovery", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Project Type: ${data.discovery.category || "Not specified"}`, 20, y);
  y += 6;

  if (data.discovery.painPoints.length > 0) {
    doc.text(`Pain Points: ${data.discovery.painPoints.join(", ")}`, 20, y);
    y += 6;
  }

  if (data.discovery.notes) {
    const notesLines = doc.splitTextToSize(`Notes: ${data.discovery.notes}`, pageWidth - 40);
    doc.text(notesLines, 20, y);
    y += notesLines.length * 5 + 6;
  }
  y += 5;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Site Assessment", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Layout Type: ${data.config.layoutType || "Not specified"}`, 20, y);
  y += 6;
  doc.text(`Window in Shower: ${data.config.hasWindow ? "Yes" : "No"}`, 20, y);
  y += 6;
  doc.text(`Obstructions: ${data.config.obstructions?.length ? data.config.obstructions.join(", ") : "None"}`, 20, y);
  y += 10;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Measurements", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const wm = data.config.wallMeasurements;
  if (wm?.wallA?.width || wm?.wallA?.height) {
    doc.text(`Wall A: ${wm.wallA.width}" × ${wm.wallA.height}"`, 20, y);
    y += 6;
  }
  if (wm?.wallB?.width || wm?.wallB?.height) {
    doc.text(`Wall B: ${wm.wallB.width}" × ${wm.wallB.height}"`, 20, y);
    y += 6;
  }
  if (wm?.wallC?.width || wm?.wallC?.height) {
    doc.text(`Wall C: ${wm.wallC.width}" × ${wm.wallC.height}"`, 20, y);
    y += 6;
  }
  if (data.config.drainLocation) {
    doc.text(`Drain Location: ${data.config.drainLocation}`, 20, y);
    y += 6;
  }
  y += 5;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Structural Changes", 20, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const sc = data.config.structuralChanges;
  doc.text(`Removing Walls: ${sc?.removingWalls ? "Yes" : "No"}`, 20, y);
  y += 6;
  doc.text(`Adding Walls: ${sc?.addingWalls ? "Yes" : "No"}`, 20, y);
  y += 6;
  doc.text(`Moving Drain: ${sc?.movingDrain ? "Yes" : "No"}`, 20, y);
  y += 6;
  doc.text(`Moving Plumbing: ${sc?.movingPlumbing ? "Yes" : "No"}`, 20, y);
  y += 6;
  doc.text(`Subfloor Modification: ${sc?.subfloorMod ? "Yes" : "No"}`, 20, y);
  y += 6;
  doc.text(`Window Modification: ${sc?.windowMod || "N/A"}`, 20, y);
  y += 10;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Design Selections", 20, y);
  y += 8;

  const scopeConfigs = data.isMultiScope ? data.completedScopes : [{ config: data.config }];
  for (let i = 0; i < scopeConfigs.length; i++) {
    const scope = scopeConfigs[i];
    const items = deriveBuildSummaryItems(scope.config, data.allOptions);
    const scopeLabel = data.isMultiScope ? `Scope ${i + 1}: ${scope.category}` : "Work Order";

    if (y > 230) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(11);
    doc.text(scopeLabel, 20, y);
    y += 6;

    doc.setFontSize(9);
    for (const item of items) {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${item.label}: ${item.value}`, 25, y);
      y += 5;
    }
    y += 5;
  }

  if (data.config.discounts) {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Discounts Applied", 20, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const discountItems: string[] = [];
    if (data.config.discounts.military) discountItems.push("Military");
    if (data.config.discounts.senior) discountItems.push("Senior");
    if (data.config.discounts.frontliner) discountItems.push("Frontliner");

    if (discountItems.length > 0) {
      doc.text(discountItems.join(", "), 20, y);
      y += 6;
    } else {
      doc.text("No discounts applied", 20, y);
      y += 6;
    }
  }

  y += 10;
  if (y > 240) {
    doc.addPage();
    y = 20;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Pricing Summary", 20, y);
  y += 8;

  autoTable(doc, {
    startY: y,
    head: [["Description", "Amount"]],
    body: [
      ["Standard Price", `$${data.pricing.standardPrice.toLocaleString()}`],
      ["Promotional Price", `$${data.pricing.promoPrice.toLocaleString()}`],
      ["Contract Total", `$${data.pricing.chosenPrice.toLocaleString()}`],
      ["Payment Method", data.pricing.selectedPayment === "financing" ? "Financing" : "Cash / Check"],
      ["Deposit Due Today", data.pricing.selectedPayment === "financing" ? "$0" : `$${data.pricing.depositAmount.toLocaleString()}`],
    ],
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 40, halign: "right" },
    },
    margin: { left: 20, right: 20 },
  });

  y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 15;

  if (y > 250) {
    doc.addPage();
    y = 20;
  }

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Signatures", 20, y);
  y += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  if (data.homeownerSig) {
    try {
      doc.addImage(data.homeownerSig, "PNG", 20, y, 60, 20);
      y += 25;
      doc.text(`Homeowner: ${data.arrival.name}`, 20, y);
      if (data.homeownerSignedAt) {
        doc.text(`Signed: ${new Date(data.homeownerSignedAt).toLocaleString()}`, 20, y + 5);
      }
      y += 15;
    } catch {
      doc.text("Homeowner: Signed", 20, y);
      y += 6;
    }
  }

  if (data.repSig) {
    try {
      doc.addImage(data.repSig, "PNG", 20, y, 60, 20);
      y += 25;
      doc.text("Company Representative", 20, y);
      if (data.repSignedAt) {
        doc.text(`Signed: ${new Date(data.repSignedAt).toLocaleString()}`, 20, y + 5);
      }
      y += 15;
    } catch {
      doc.text("Company Representative: Signed", 20, y);
      y += 6;
    }
  }

  y += 10;
  if (y > 260) {
    doc.addPage();
    y = 20;
  }

  doc.setDrawColor(200);
  doc.line(20, y, pageWidth - 20, y);
  y += 10;

  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("Terms & Conditions:", 20, y);
  y += 6;
  doc.text("• The customer agrees to the terms and conditions and authorizes the company to perform the work at the stated price.", 20, y);
  y += 5;
  doc.text("• The customer acknowledges the 3-business-day right of rescission.", 20, y);
  y += 5;
  doc.text("• All build configuration, pricing, and materials are locked. Any changes require a Change Order.", 20, y);

  doc.setFontSize(8);
  doc.text(`Generated by Koredor on ${new Date().toLocaleString()}`, pageWidth / 2, 290, { align: "center" });

  const fileName = `Contract_${data.contractId}_${data.arrival.name.replace(/\s+/g, "_")}.pdf`;
  doc.save(fileName);
}
